"use client";

import { useCallback, useState } from "react";

import type { TokenInfo } from "@/domain/types";

function isSameToken(a: TokenInfo | null, b: TokenInfo | null): boolean {
  return Boolean(a && b && a.address.toLowerCase() === b.address.toLowerCase());
}

interface PairState {
  tokenA: TokenInfo;
  tokenB: TokenInfo | null;
}

/**
 * Manages a from/to token pair with the constraint every swap/liquidity
 * form needs: the two sides can never hold the same token. Picking a token
 * already on the other side swaps the pair instead of producing a
 * duplicate; if there's nothing to swap into (the other side is still
 * unset), the pick is ignored.
 */
export function useTokenPair(defaultTokenA: TokenInfo, defaultTokenB: TokenInfo | null = null) {
  const [{ tokenA, tokenB }, setPair] = useState<PairState>({ tokenA: defaultTokenA, tokenB: defaultTokenB });

  const setTokenA = useCallback((next: TokenInfo) => {
    setPair((prev) => (isSameToken(next, prev.tokenB) ? { tokenA: next, tokenB: prev.tokenA } : { tokenA: next, tokenB: prev.tokenB }));
  }, []);

  const setTokenB = useCallback((next: TokenInfo) => {
    setPair((prev) => {
      if (!isSameToken(next, prev.tokenA)) return { tokenA: prev.tokenA, tokenB: next };
      // Selected A's own token for B — swap if there's a previous B to swap
      // into A with, otherwise there's nothing sensible to do, so ignore.
      return prev.tokenB ? { tokenA: prev.tokenB, tokenB: next } : prev;
    });
  }, []);

  const swapTokens = useCallback(() => {
    setPair((prev) => (prev.tokenB ? { tokenA: prev.tokenB, tokenB: prev.tokenA } : prev));
  }, []);

  const setPairDirect = useCallback((next: PairState) => {
    setPair(next);
  }, []);

  return { tokenA, tokenB, setTokenA, setTokenB, swapTokens, setPairDirect };
}
